import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { loanPresets, otherLoanPresets, LoanPreset } from '@/lib/loanCalculator';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LoanTypeSelectorProps {
  selected: string;
  onSelect: (preset: LoanPreset) => void;
}

export function LoanTypeSelector({ selected, onSelect }: LoanTypeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  const isOtherSelected = otherLoanPresets.some(p => p.id === selected);
  const selectedOther = otherLoanPresets.find(p => p.id === selected);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {loanPresets.map((preset, index) => (
        <motion.button
          key={preset.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.03 }}
          onClick={() => onSelect(preset)}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200",
            selected === preset.id
              ? "bg-gradient-primary text-primary-foreground shadow-sm"
              : "bg-card border border-border hover:border-primary/30 hover:bg-secondary/50 text-foreground"
          )}
        >
          <span className="text-sm">{preset.icon}</span>
          <span>{preset.name}</span>
        </motion.button>
      ))}
      
      {/* Other Dropdown */}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: loanPresets.length * 0.03 }}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200",
              isOtherSelected
                ? "bg-gradient-primary text-primary-foreground shadow-sm"
                : "bg-card border border-border hover:border-primary/30 hover:bg-secondary/50 text-foreground"
            )}
          >
            <span className="text-sm">{isOtherSelected && selectedOther ? selectedOther.icon : '📋'}</span>
            <span>{isOtherSelected && selectedOther ? selectedOther.name : 'Other'}</span>
            <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
          </motion.button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <AnimatePresence>
            {otherLoanPresets.map((preset, index) => (
              <DropdownMenuItem
                key={preset.id}
                onClick={() => {
                  onSelect(preset);
                  setIsOpen(false);
                }}
                className={cn(
                  "flex items-center gap-2 cursor-pointer",
                  selected === preset.id && "bg-primary/10 text-primary"
                )}
              >
                <span className="text-base">{preset.icon}</span>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{preset.name}</span>
                  <span className="text-xs text-muted-foreground">{preset.defaultRate}% rate</span>
                </div>
              </DropdownMenuItem>
            ))}
          </AnimatePresence>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
